/**
 * Estimate-pricing control for quote and lead screens.
 * Opens EstimatePricingPanel (live rate card). Does not commit a quote total.
 *
 * Prefer placement="inline" (toolbar / pricing section) so the control never
 * covers modal or form footer actions. placement="fixed" remains available
 * with a footer-clearing bottom offset.
 */

import React, { useState } from "react";
import { Fab, Tooltip, Box, Button } from "@mui/material";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import styled from "styled-components";
import EstimatePricingPanel from "./EstimatePricingPanel.jsx";

/** Fixed FAB sits above typical Ant Design / MUI modal footers (~64–80px). */
const FloatingEstimateButton = styled(Fab)`
  && {
    position: fixed;
    right: 24px;
    bottom: 96px;
    z-index: 1050;
  }
`;

/**
 * @param {object} props
 * @param {string|null} [props.leadId]
 * @param {string|null} [props.quoteId]
 * @param {object} [props.formSnapshot]
 * @param {"inline"|"fixed"} [props.placement]
 */
const EstimatePricingFab = ({
  leadId = null,
  quoteId = null,
  formSnapshot = {},
  placement = "inline",
}) => {
  const [open, setOpen] = useState(false);
  const canOpen = Boolean(leadId);

  const tooltipTitle = canOpen
    ? "Estimate pricing from the live rate card"
    : "Link a lead first to estimate pricing";

  const openButton =
    placement === "fixed" ? (
      <FloatingEstimateButton
        color="primary"
        variant="extended"
        disabled={!canOpen}
        onClick={() => setOpen(true)}
        aria-label="Estimate pricing"
        data-testid="estimate-pricing-open"
      >
        <CalculateOutlinedIcon sx={{ mr: 1 }} />
        Estimate pricing
      </FloatingEstimateButton>
    ) : (
      <Button
        color="primary"
        variant="outlined"
        size="medium"
        disabled={!canOpen}
        onClick={() => setOpen(true)}
        startIcon={<CalculateOutlinedIcon />}
        aria-label="Estimate pricing"
        data-testid="estimate-pricing-open"
      >
        Estimate pricing
      </Button>
    );

  return (
    <Box data-testid="estimate-pricing-fab">
      {!open && (
        <Tooltip title={tooltipTitle}>
          <Box component="span">{openButton}</Box>
        </Tooltip>
      )}
      <EstimatePricingPanel
        open={open}
        onClose={() => setOpen(false)}
        leadId={leadId}
        quoteId={quoteId}
        formSnapshot={formSnapshot}
      />
    </Box>
  );
};

export default EstimatePricingFab;
